import * as THREE from 'three'
import { gsap } from 'gsap'

import { CSS2DRenderer, CSS2DObject } from 'three/addons/renderers/CSS2DRenderer.js'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import mapping from './mapping.js'
import { createParticles, updateParticles } from './particles.js'

/**
 * Music
 */
const bgm = new Audio("/Johnny Mathis - It's Beginning to Look Like Christmas (Official Instrumental Version).mp3")
bgm.volume = 0.2



/**
 * Loaders
 */
let loadComplete = false
const loadingBarElement = document.querySelector('.loading-bar')
const loadingManager = new THREE.LoadingManager(
    // Loaded
    () =>
    {
        loadComplete = true
        console.log('load complete', loadComplete)
        
        // Wait a little
        window.setTimeout(() =>
        {
            bgm.play()
            generateTitle()

            // Animate overlay
            gsap.to(overlayMaterial.uniforms.uAlpha, { 
                duration: 2, 
                value: 0, 
                // delay: 1,
                onComplete: () => {
                    // generateTitle()
                }
            })

            // Update loadingBarElement
            loadingBarElement.classList.add('ended')
            loadingBarElement.style.transform = ''
        }, 500)
    },

    // Progress
    (itemUrl, itemsLoaded, itemsTotal) =>
    {
        // Calculate the progress and update the loadingBarElement
        const progressRatio = itemsLoaded / itemsTotal
        loadingBarElement.style.transform = `scaleX(${progressRatio})`
    }
)

// TextureLoader
const textureLoader = new THREE.TextureLoader(loadingManager)

// Draco loader
const dracoLoader = new DRACOLoader(loadingManager)
dracoLoader.setDecoderPath('draco/')

// GLTF loader
const gltfLoader = new GLTFLoader(loadingManager)
gltfLoader.setDRACOLoader(dracoLoader)

/**
 * Base
 */
const sizes = {
    width: window.innerWidth,
    height: window.innerHeight
}

window.addEventListener('resize', () =>
{
    // Update sizes
    sizes.width = window.innerWidth
    sizes.height = window.innerHeight

    // Update camera
    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()

    // Update renderer
    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))

    labelRenderer.setSize(sizes.width, sizes.height)

    // effectComposer.setSize(sizes.width, sizes.height)
    // effectComposer.setPixelRatio(Math.min(window.devicePixelRatio, 2))

    // effectFXAA.uniforms["resolution"].value.set(
    //     1 / window.innerWidth,
    //     1 / window.innerHeight
    // )

    title.element.style.fontSize = `${sizes.width / 10}px`
})

// Canvas
const canvas = document.querySelector('canvas.webgl')

// Scene
const scene = new THREE.Scene()

const axesHelper = new THREE.AxesHelper(5)
const gridHelper = new THREE.GridHelper(100, 100)

/**
 * Overlay
 */
const overlayGeometry = new THREE.PlaneGeometry(2, 2, 1, 1)
const overlayMaterial = new THREE.ShaderMaterial({
    // wireframe: true,
    transparent: true,
    uniforms:
    {
        uAlpha: { value: 1 }
    },
    vertexShader: `
        void main()
        {
            gl_Position = vec4(position, 1.0);
        }
    `,
    fragmentShader: `
        uniform float uAlpha;

        void main()
        {
            gl_FragColor = vec4(0.0, 0.0, 0.0, uAlpha);
        }
    `
})
const overlay = new THREE.Mesh(overlayGeometry, overlayMaterial)
scene.add(overlay)

/**
 * Textures
 */

const particleTexture = textureLoader.load('/textures/particles/1.png')
const nightMatcap = textureLoader.load('/matcap.png')
nightMatcap.colorSpace = THREE.SRGBColorSpace

const materials = {}

Object.entries(mapping).forEach(([objectName, textureFile]) => {
  const texture = textureLoader.load(`/gltf/jpg/${textureFile}`)
  texture.colorSpace = THREE.SRGBColorSpace
  texture.flipY = false
  materials[objectName] = new THREE.MeshBasicMaterial({ map: texture })
})

/**
 * Skybox 
 */
const box = new THREE.Mesh(
    new THREE.BoxGeometry(40, 30, 30),
    new THREE.MeshMatcapMaterial({
        matcap: nightMatcap,
        side: THREE.BackSide
    })
)
box.position.y = 15
scene.add(box)

/**
 * Models
 */

const elves = {}
let elvesLoaded = false

gltfLoader.load(
    '/gltf/santas-workshop.glb',
    (gltf) =>
    {
        gltf.scene.scale.set(0.5, 0.5, 0.5)
        gltf.scene.rotation.y = Math.PI * 0.5

        gltf.scene.traverse((child) => {
            if (child.isMesh) {
                const material = materials[child.name]
                if (material) {
                    child.material = material
                }
            } else if (child.isGroup) {
                child.traverse((mesh) => {
                    if (mesh instanceof THREE.Mesh) {
                        const material = materials[child.name]
                        if (material) {
                            mesh.material = material
                        }
                    }
                })
            } else if (child.name.includes('metarig')) {
                child.traverse((group) => {
                    if (group.isGroup) {
                        group.traverse((mesh) => {
                            if ( mesh.isMesh ) {
                                const material = materials['elf']
                                if (material) {
                                    mesh.material = material
                                }
                            }
                        })
                        elves[group.name] = group
                    }
                })
            }
        })
    
        elvesLoaded = true
        scene.add(gltf.scene)
    }
)

/**
 * General labels
 */
let generatedText = false
const labels = {}
const elvesTexts = {
    'hammerElf': 'Crafted with the rarest magic found only at the North Pole',
    'letterElf': 'Every wish and whisper reaches the heart of Santa',
    'sewingElf': 'Bundled with love and sealed with magic'
}  

// Move camera to elf
const elfOffset = {
    "sewingElf": new THREE.Vector3(-3, 1, 6),
    "hammerElf": new THREE.Vector3(-2, 0.5, 6),
    "letterElf": new THREE.Vector3(2, 1, 5),
}

const labelOffset = {
    "sewingElf": new THREE.Vector3(-1, 1, 0),
    "hammerElf": new THREE.Vector3(2, 0, 0),
    "letterElf": new THREE.Vector3(2, 0, 0),
}

const lookAtOffset = {
    "sewingElf": new THREE.Vector3(-2, 1, 0),
    "hammerElf": new THREE.Vector3(1, 0, 0),
    "letterElf": new THREE.Vector3(2, 0, 0),
}

const generateText = () => {
    Object.values(elves).forEach((elf, i) => {
        if (elf.name !== "cakeElf") {
            const div = document.createElement('div')
            div.className = 'label'
            div.textContent = elvesTexts[elf.name]
            div.style.opacity = 0 // Start hidden
            div.style.fontSize =  `${sizes.width / 30}px`
    
            const label = new CSS2DObject(div)
            label.position.set(
                labelOffset[elf.name].x * 2, 
                labelOffset[elf.name].y * 2, 
                labelOffset[elf.name].z * 2)
            elf.add(label)
            labels[elf.name] = label
        }
    })
}

let title
const generateTitle = () => {
    const div = document.createElement('div')
    div.className = 'title'
    div.textContent = "Santa's Workshop"
    div.style.opacity = 0
    div.style.fontSize = `${sizes.width / 10}px`

    title = new CSS2DObject(div)
    title.position.set(0, 8, 0)

    scene.add(title)

    gsap.to(title.element, { 
        opacity: 1, 
        duration: 1,
        onComplete: () => {
            fadeOutTitle()
        }
    })
}

function fadeInLabel(labelKey) {
    gsap.to(labels[labelKey].element, { opacity: 1, duration: 1 })
}

function fadeOutLabels() {
    Object.values(labels).forEach((label) => {
        gsap.to(label.element, { opacity: 0, duration: 1 })
    })
}

function fadeOutTitle() {
    if (title) {
        gsap.to(title.element, { opacity: 0, duration: 1 })
    }
}


/**
 * Particles
 */
// SNOW
const snow = createParticles({
    'texture': particleTexture,
    'count': 1000, 
    'color': new THREE.Vector3(1, 1, 1),
    'yDirection': -1, 
    'boundingBox': new THREE.Vector3(40, 30, 40), 
    'offset': new THREE.Vector3(0, 0, 0), 
    'size': 0.2,
    'straight': false
})
scene.add(snow)

// MAGIC DUST
let generatedDusts = false
const dustCount = 20
const magicDusts = []
const yStarts = []
const createMagicDusts = () => {
    Object.values(elves).forEach((elf) => {
        if (elf !== "cakeElf") {
            const elfPosition = elf.getWorldPosition(new THREE.Vector3())
            const dust = createParticles({
                'texture': particleTexture,
                'count': dustCount, 
                'color': new THREE.Vector3(1, 0.8, 0.2),
                'yDirection': 1, 
                'boundingBox': new THREE.Vector3(1, 1, 1), 
                'offset': elfPosition, 
                'size': 0.1,
                'straight': true,
            })
            magicDusts.push(dust)
            yStarts.push(elfPosition.y)
            scene.add(dust)
        }
    })
    generatedDusts = true
}




/**
 * Camera
 */
// Base camera
const camera = new THREE.PerspectiveCamera(50, sizes.width / sizes.height, 0.1, 100)
camera.position.set(0, 9, 24)
camera.lookAt(0, 9, 0)
scene.add(camera)

// const controls = new OrbitControls(camera, canvas)
// controls.enableDamping = true

let centerX = camera.position.x
let centerY = camera.position.y

let isMoving = false

window.addEventListener('mousemove', (event) => {
    if (camera && !isMoving) {
        const x = event.clientX
        const y = event.clientY
    
        const normalX = x / sizes.width - 0.5
        const normalY = y / sizes.height - 0.5
    
        const parallaxX = normalX * 0.5 + centerX 
        const parallaxY = -normalY * 0.5 + centerY
    
        if (!isNaN(parallaxX) && !isNaN(parallaxY)) {
            camera.position.x += (parallaxX - camera.position.x) * 5 * 16 * 0.001
            camera.position.y += (parallaxY - camera.position.y) * 5 * 16 * 0.001
        }
    }
})




function moveToElf(elfName) {
    const elf = elves[elfName]
    const elfPosition = elf.getWorldPosition(new THREE.Vector3())
        
    isMoving = true
    gsap.to(camera.position, {
        x: elfPosition.x + elfOffset[elfName].x + labelOffset[elfName].x,
        y: elfPosition.y + elfOffset[elfName].y,
        z: elfPosition.z + elfOffset[elfName].z,
        duration: 2,
        ease: 'power1.out',
        onUpdate: () => {
            camera.lookAt(
                elfPosition.x + lookAtOffset[elfName].x, 
                elfPosition.y, 
                elfPosition.z
            )
        },
        onComplete: () => {
            centerX = camera.position.x
            centerY = camera.position.y
            isMoving = false
            fadeInLabel(elfName)
        }
    })
}

function moveToCenter() {
    isMoving = true
    fadeOutLabels()

    gsap.to(camera.position, {
        x: 0,
        y: 9,
        z: 24,
        duration: 2,
        ease: 'power1.out',
        onUpdate: () => {
            camera.lookAt(0, 9, 0)
        },
        onComplete: () => {
            centerX = camera.position.x
            centerY = camera.position.y
            isMoving = false
            // updateTextVisibility(Object.keys(elves).indexOf(elfName))
        }
    })
}

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
    canvas: canvas,
    antialias: true
})
renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))

/**
 * CSS2DRenderer
 */

const labelRenderer = new CSS2DRenderer()
labelRenderer.setSize( window.innerWidth, window.innerHeight )
labelRenderer.domElement.style.position = 'absolute'
labelRenderer.domElement.style.top = '0px'
labelRenderer.domElement.style.pointerEvents = 'none'
document.body.appendChild( labelRenderer.domElement )

/**
 *  Raycasting 
 */
const raycaster = new THREE.Raycaster()
const pointer = new THREE.Vector2()

function raycast( event ) {
    if (elvesLoaded) {

        pointer.x = ( event.clientX / window.innerWidth ) * 2 - 1
        pointer.y = - ( event.clientY / window.innerHeight ) * 2 + 1

        raycaster.setFromCamera( pointer, camera )
        const intersects = raycaster.intersectObjects( Object.values(elves) )
        
        if ( intersects.length > 0 ) {
            for ( let i = 0; i < intersects.length; i ++ ) {
                const object = intersects[ i ].object
                moveToElf(object.parent.name)
            }
        } else {
            moveToCenter()
        }
    }
}

window.addEventListener( 'click', raycast )

/**
 * Animate
 */
const clock = new THREE.Clock()
let elapsedSeconds = clock.getElapsedTime()
const tick = () =>
{
    // controls.update()
    if (loadComplete) {
        if ( !generatedText) {
            generateText()
            generatedText = true
        } 
        if ( !generatedDusts) {
            createMagicDusts()
        }
    }


    // Update particles
    elapsedSeconds = clock.getElapsedTime()
    if (snow && snow.geometry.attributes.position && snow.geometry.attributes.velocity) {
        updateParticles(snow, elapsedSeconds, -1, 0, 1000)
    }

    if (generatedDusts) {
        magicDusts.forEach((dust, i) => {
            updateParticles(dust, elapsedSeconds, 1, yStarts[i], dustCount)
        })
    }

    // Render
    renderer.render(scene, camera)
    // effectComposer.render()
    labelRenderer.render(scene, camera)

    // Call tick again on the next frame
    window.requestAnimationFrame(tick)

}

tick()
